import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ChevronDown, RefreshCw, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { cn, formatMoney } from '@/lib/utils'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { applyQuoteChange, invalidateQuoteOverview, quoteQueryOptions } from '../api/queries'
import { activityLabel } from '../ui/format'
import { ErrorState, LoadingState, PageHeader, SurfaceCard } from '../ui/page'
import { QuoteLineIdentity } from '../ui/product'
import { Money } from '../ui/values'
import { publishingApi, quoteActivityQueryOptions, quoteVersionsQueryOptions, type QuoteActivity, type QuoteVersion } from './api'
import { compareVersions, snapshotOf, type VersionSnapshot } from './version-diff'

const dateTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' })
const stageNames: Record<string, string> = {
  customer: 'Customer',
  equipment: 'Equipment',
  pricing: 'Pricing',
  shipping: 'Shipping',
  services: 'Services',
  tax: 'Tax',
  financing: 'Payment',
  approval: 'Review',
  invoicing: 'Start',
}

type ShippingSide = { selected_method?: string | null; freight_override?: string | null } | null

/** A freight price set by hand or no freight chosen (USA-1044), named with its amount. */
function freightChoice(payload: unknown) {
  const { before, after } = (payload ?? {}) as { before?: ShippingSide; after?: ShippingSide }
  if (after?.selected_method === 'manual_freight') {
    const changed =
      before?.selected_method !== 'manual_freight' || Number(before.freight_override) !== Number(after.freight_override)
    return changed ? `Freight price set: ${formatMoney(after.freight_override)}` : null
  }
  if (after?.selected_method === 'no_freight' && before?.selected_method !== 'no_freight') {
    return 'No freight quote needed'
  }
  return null
}

function changeLabel(activity: QuoteActivity | undefined) {
  const stage = (activity?.payload as { stage?: unknown } | undefined)?.stage
  if (activity?.activity_type === 'workflow_stage_updated' && typeof stage === 'string' && stageNames[stage]) {
    return `${stageNames[stage]} updated`
  }
  if (activity?.activity_type === 'shipping_updated') {
    const choice = freightChoice(activity.payload)
    if (choice) return choice
  }
  return activityLabel(activity?.activity_type)
}

const compareOptions = [
  { value: 'previous', label: 'Changes in this version' },
  { value: 'current', label: 'Compare with current' },
] as const

function VersionDetail({
  version,
  previous,
  current,
}: {
  version: QuoteVersion
  previous: QuoteVersion | undefined
  current: QuoteVersion | undefined
}) {
  const [against, setAgainst] = useState<(typeof compareOptions)[number]['value']>('previous')
  const snapshot = snapshotOf(version.snapshot)
  const isCurrent = current?.id === version.id
  const comparison =
    against === 'previous'
      ? compareVersions(snapshotOf(previous?.snapshot), snapshot)
      : compareVersions(snapshot, snapshotOf(current?.snapshot))
  const lines = snapshot.lines ?? []

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 border-t bg-muted/20 px-4 py-4">
      {!isCurrent ? (
        <ReviewChoiceGroup label="Compare" onChange={setAgainst} options={compareOptions} value={against} />
      ) : null}
      <section aria-label="Changes">
        {comparison.lines.length || comparison.totals.length ? (
          <ul className="grid gap-2">
            {comparison.lines.map((change) => (
              <li className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm" key={`${change.kind}-${change.line.id}`}>
                <StatusPill
                  tone={change.kind === 'added' ? 'success' : change.kind === 'removed' ? 'danger' : 'progress'}
                >
                  {change.kind === 'added' ? 'Added' : change.kind === 'removed' ? 'Removed' : 'Changed'}
                </StatusPill>
                <span className="min-w-0 flex-1 break-words font-medium">
                  {change.line.model || change.line.description}
                  {change.fields.length ? (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {change.fields
                        .map(
                          (field) =>
                            `${field.label}: ${field.money ? formatMoney(field.before) : (field.before ?? '—')} → ${
                              field.money ? formatMoney(field.after) : (field.after ?? '—')
                            }`,
                        )
                        .join(' · ')}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
            {comparison.totals.map((total) => (
              <li className="flex flex-wrap items-center gap-x-3 text-sm" key={total.label}>
                <StatusPill tone="neutral">{total.label}</StatusPill>
                <span className="tabular-nums">
                  {formatMoney(total.before)} → {formatMoney(total.after)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No item or total changes.</p>
        )}
      </section>
      <section aria-label={`Items in revision ${version.version}`} className="grid grid-cols-[minmax(0,1fr)] gap-2">
        {lines.map((line) => (
          <div
            className={cn('flex min-w-0 items-start justify-between gap-3', line.parent_line_id && 'ml-6 border-l pl-3')}
            key={line.id}
          >
            <QuoteLineIdentity className="min-w-0 flex-1" compact line={line} />
            <span className="shrink-0 text-right text-sm tabular-nums">
              <span className="block text-xs text-muted-foreground">Qty {Number(line.quantity)}</span>
              <Money value={line.totals?.sell} />
            </span>
          </div>
        ))}
        {snapshot.totals ? (
          <div className="flex justify-between border-t pt-2 text-sm font-semibold">
            <span>Total</span>
            <Money value={snapshot.totals.grand_total} />
          </div>
        ) : null}
      </section>
    </div>
  )
}

/** Every saved change is a version: view it, compare it and restore it (demo `QuoteVersionHistory`). */
export function VersionHistoryPage({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const quoteQuery = useQuery(quoteQueryOptions(api, quoteId))
  const versions = useQuery(quoteVersionsQueryOptions(api, quoteId, quoteQuery.data?.revision))
  const activity = useQuery(quoteActivityQueryOptions(api, quoteId))
  const [open, setOpen] = useState<number | null>(null)
  const restore = useMutation({
    mutationFn: (version: number) => publishingApi.restore(api, quoteQuery.data!, version),
    onSuccess: (quote) => {
      applyQuoteChange(queryClient, quoteId, quote)
      void queryClient.invalidateQueries({ queryKey: ['quote-versions', quoteId] })
      void queryClient.invalidateQueries({ queryKey: ['quote-activity', quoteId] })
      void invalidateQuoteOverview(queryClient)
      setOpen(null)
    },
  })

  if (quoteQuery.isLoading || versions.isLoading) return <LoadingState label="Loading version history" />
  if (!quoteQuery.data || !versions.data) {
    return (
      <ErrorState
        description="The quote history could not be loaded."
        onRetry={() => {
          void quoteQuery.refetch()
          void versions.refetch()
        }}
        title="History unavailable"
      />
    )
  }
  const quote = quoteQuery.data
  const list = versions.data
  // A version and its activity row are written together, so they share a timestamp.
  const changeByTime = new Map((activity.data ?? []).map((item) => [item.created_at, item]))
  const stale = restore.error instanceof QuoteBuilderApiError && restore.error.status === 412

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        actions={
          <Button asChild size="sm" variant="outline">
            <Link params={{ quoteId, stage: 'equipment' }} to="/quotes/$quoteId/workflow/$stage">
              <ArrowLeft aria-hidden="true" />
              Back to quote
            </Link>
          </Button>
        }
        description="Every saved change creates a version you can restore."
        title="Version history"
      />
      {restore.error ? (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          <span>{stale ? 'The quote changed. Reload the history before restoring.' : 'The version could not be restored.'}</span>
          {stale ? (
            <Button
              onClick={() => {
                restore.reset()
                void quoteQuery.refetch()
                void versions.refetch()
              }}
              size="sm"
              variant="outline"
            >
              <RefreshCw aria-hidden="true" />
              Reload
            </Button>
          ) : null}
        </div>
      ) : null}
      <SurfaceCard>
        <ul className="divide-y">
          {list.map((version, index) => {
            const snapshot: VersionSnapshot = snapshotOf(version.snapshot)
            const expanded = open === version.version
            const isCurrent = version.version === quote.revision
            const change = changeByTime.get(version.created_at)
            return (
              <li key={version.id}>
                <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
                  <span className="grid size-10 place-items-center rounded-lg bg-muted text-xs font-bold tabular-nums">
                    v{version.version}
                  </span>
                  <button
                    aria-expanded={expanded}
                    data-version-toggle
                    className="flex min-w-0 items-center gap-2 rounded-md text-left focus-visible:outline-2 focus-visible:outline-ring"
                    onClick={() => setOpen(expanded ? null : version.version)}
                    type="button"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{changeLabel(change)}</span>
                      <span className="block text-xs text-muted-foreground">
                        {dateTime.format(new Date(version.created_at))}
                        {change?.actor ? ` · ${change.actor.name}` : ''} · {snapshot.lines?.length ?? 0} {snapshot.lines?.length === 1 ? 'item' : 'items'}
                        {snapshot.totals ? ` · ${formatMoney(snapshot.totals.grand_total)}` : ''}
                      </span>
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn('size-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')}
                    />
                  </button>
                  <div className="col-span-2 sm:col-span-1">
                    {isCurrent ? (
                      <StatusPill tone="success">Current</StatusPill>
                    ) : (
                      <LoadingButton
                        disabled={restore.isPending}
                        loading={restore.isPending && restore.variables === version.version}
                        loadingText="Restoring..."
                        onClick={() => restore.mutate(version.version)}
                        size="sm"
                        variant="outline"
                      >
                        <RotateCcw aria-hidden="true" />
                        Restore
                      </LoadingButton>
                    )}
                  </div>
                </div>
                {expanded ? <VersionDetail current={list[0]} previous={list[index + 1]} version={version} /> : null}
              </li>
            )
          })}
        </ul>
      </SurfaceCard>
    </div>
  )
}
